import { SystemRoles } from '@/backend/extraTypes';
import { getBackgroundCheckExpirationStorageFilters } from '@/backend/features/background-check/utils/backgroundCheckExpirationUtils';
import { getBackgroundCheckStorage } from '@/backend/features/background-check/utils/getBackgroundCheckStorage';
import { ExtendedAccountColumns } from '@/backend/features/contact-management/entities/extended-account/types';
import { getExtendedAccountStorage } from '@/backend/features/contact-management/utils/getExtendedAccountStorage';
import { screeningsRoutes } from '@/backend/routes';
import { prepareNotificationsForAccounts, replaceNotificationKeysWithNumbers } from '@/backend/utils';
import { checkPreviousBackgroundTaskRunTime, getBackgroundTaskState } from '@/backend/utils/backgroundTaskUtils';
import { escapeHtml } from '@juvare/common-form-ui/WidgetForm/Utils';
import {
    BackgroundTaskStateColumns,
    BackgroundTaskStateData,
} from '@juvare/contact-management/backend/features/background-task-state/shared';
import { getLicenseCheckLatestStatusStorage } from '@juvare/credentialing/backend/features/credentialing-verification/utils/getLicenseCheckLatestStatusStorage';
import { getManualCredentialVerificationExpirationStorageFilters } from '@juvare/credentialing/backend/features/credentialing-verification/utils/manualCredentialVerificationExpirationUtils';
import { batchFunction } from '@juvare/credentialing/backend/utils';
import { AppContext, BackgroundTaskDef } from '@juvare/platform-app/App';
import { StorageBackendApi } from '@juvare/platform-app/Storage';
import { AppState } from '@juvare/platform-sdk-react/types';
import { minutesToMilliseconds } from 'date-fns';
import { millisecondsInDay } from 'date-fns/constants';

export const expiringChecksAdminNotificationTaskName = 'expiringBackgroundCheckNotification';
const taskFrequencyProd = millisecondsInDay;
const taskFrequencyDev = minutesToMilliseconds(5);
const rolesToNotify: SystemRoles[] = ['system-admin', 'client-admin'];

export const expiringChecksAdminNotificationTask: BackgroundTaskDef<AppState> = {
    callback: (context) => {
        const { taskStorage, taskState } = getBackgroundTaskState(context, expiringChecksAdminNotificationTaskName);
        const { hasTimePassed, lastRunDateTime } = checkPreviousBackgroundTaskRunTime(
            context.environment,
            taskState?.columns.lastRunTimestamp,
            taskFrequencyDev,
            taskFrequencyProd,
        );
        if (!hasTimePassed) {
            return;
        }

        const backgroundCheckExpiringCounts = getBackgroundCheckCounts(context, lastRunDateTime);
        const credentialVerificationExpiringCounts = getManualCredentialVerificationCounts(context, lastRunDateTime);

        sendNotifications(context, backgroundCheckExpiringCounts, credentialVerificationExpiringCounts);

        finishTask(taskStorage);
    },
    delaySeconds: 300,
};

function finishTask(storage: StorageBackendApi<BackgroundTaskStateData, BackgroundTaskStateColumns>): void {
    storage.post({
        action: 'create-or-update',
        uniqueColumn: 'taskName',
        data: {
            taskName: expiringChecksAdminNotificationTaskName,
            lastRunTimestamp: Date.now(),
        },
    });
}

function getBackgroundCheckCounts(
    context: AppContext<AppState>,
    lastRunDateTime: Date | undefined,
): {
    expiring: number;
    expired: number;
} {
    const { expiringBackgroundCheckFilters, expiredBackgroundCheckFilters } = getBackgroundCheckExpirationStorageFilters(
        context.appState.settings.backgroundCheckExpirationPeriod,
        { previousCheckDateToExcludeResults: lastRunDateTime },
    );

    if (!expiringBackgroundCheckFilters || !expiredBackgroundCheckFilters) {
        return {
            expiring: 0,
            expired: 0,
        };
    }

    const backgroundCheckStorage = getBackgroundCheckStorage(context);

    return {
        expiring: backgroundCheckStorage.count({ filter: expiringBackgroundCheckFilters }),
        expired: backgroundCheckStorage.count({ filter: expiredBackgroundCheckFilters }),
    };
}

function getManualCredentialVerificationCounts(
    context: AppContext<AppState>,
    lastRunDateTime: Date | undefined,
): {
    expiring: number;
    expired: number;
} {
    const { expiringManualCredentialVerificationFilters, expiredManualCredentialVerificationFilters } =
        getManualCredentialVerificationExpirationStorageFilters(context.appState.settings.credentialVerificationExpirationPeriod, {
            previousCheckDateToExcludeResults: lastRunDateTime,
        });
    if (!expiringManualCredentialVerificationFilters || !expiredManualCredentialVerificationFilters) {
        return {
            expiring: 0,
            expired: 0,
        };
    }

    const licenseCheckStorage = getLicenseCheckLatestStatusStorage(context);
    return {
        expiring: licenseCheckStorage.count({ filter: expiringManualCredentialVerificationFilters }),
        expired: licenseCheckStorage.count({ filter: expiredManualCredentialVerificationFilters }),
    };
}

function sendNotifications(
    context: AppContext<AppState>,
    backgroundCheckExpiringCounts: {
        expiring: number;
        expired: number;
    },
    credentialVerificationExpiringCounts: {
        expiring: number;
        expired: number;
    },
): void {
    const { appTitle, expiringChecksAdminNotification } = context.appState.settings;
    const subject = expiringChecksAdminNotification.subjectTemplate.replace(/\${appName}/g, escapeHtml(appTitle));
    const { text: message, hasSomeNonZeroValuesDisplayed } = replaceNotificationKeysWithNumbers(
        expiringChecksAdminNotification.htmlMessageTemplate
            .replace(/\${appName}/g, escapeHtml(appTitle))
            .replace(
                /\${screeningsUrl}/g,
                escapeHtml([context.baseUrl, screeningsRoutes.root].join('/').replace(/(https?:\/\/[^/]+)\/+/g, '$1/')),
            )
            .replace(/\${baseUrl}/g, escapeHtml(context.baseUrl)),
        [
            [
                '${numberOfTotalLicenseVerifications}',
                credentialVerificationExpiringCounts.expiring + credentialVerificationExpiringCounts.expired,
            ],
            ['${numberOfExpiringLicenseVerifications}', credentialVerificationExpiringCounts.expiring],
            ['${numberOfExpiredLicenseVerifications}', credentialVerificationExpiringCounts.expired],
            ['${numberOfTotalBackgroundChecks}', backgroundCheckExpiringCounts.expiring + backgroundCheckExpiringCounts.expired],
            ['${numberOfExpiringBackgroundChecks}', backgroundCheckExpiringCounts.expiring],
            ['${numberOfExpiredBackgroundChecks}', backgroundCheckExpiringCounts.expired],
        ],
    );
    if (!hasSomeNonZeroValuesDisplayed) {
        return; // only zero's to display
    }

    const getNotificationText = (account: ExtendedAccountColumns) => ({
        fromDisplayName: context.appState.settings.appTitle,
        subject,
        htmlMessage: message
            .replace(/\${firstName}/g, escapeHtml(account.firstName))
            .replace(/\${lastName}/g, escapeHtml(account.lastName)),
    });

    const accountStorage = getExtendedAccountStorage(context);
    const accountsToNotify = accountStorage.list({
        filter: [
            { column: 'systemRole', op: 'in', value: rolesToNotify },
            { column: 'systemStatus', op: 'in', value: ['active'] },
        ],
    });

    const notifications = prepareNotificationsForAccounts(context, {
        notificationChannelSettings: expiringChecksAdminNotification.channel,
        getNotificationText,
        accountsToNotify: accountsToNotify.rows,
        getAccountData: (id) => accountStorage.get(id),
    });

    batchFunction(notifications, (items) => {
        context.notifyService.postNotification(items);
    });
}
